import { Trans, useTranslation } from "react-i18next";
import { namespaces } from "../../i18n/i18n.constants";
import { Col, Form, Row } from "react-bootstrap";
import { FaAngleDoubleLeft, FaAngleDoubleRight } from "react-icons/fa";
import { useMemo } from "react";
import { PaginationProps } from "./PaginationProps";

const Pagination = ({ currentPage, totalCount, pageSize, onPageChange, onPageSizeChange }: PaginationProps) => {
    const { t } = useTranslation();
    const tAreaNS = { ns: namespaces.aria };
    const totalPages = useMemo(() => {
        if (totalCount === 0 || pageSize === 0) {
            return 0;
        }
        return Math.ceil(totalCount / pageSize);
    }, [pageSize, totalCount]);

    const onPrevious = () => {
        onPageChange(currentPage - 1);
    };
    const onNext = () => {
        onPageChange(currentPage + 1);
    };

    const handlePageSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const elValue = Number(event.target.value);
        onPageSizeChange(elValue);
    };

    const rangeStart = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const rangeEnd = totalCount === 0 ? 0 : Math.min(currentPage * pageSize, totalCount);

    return (
        <div className="p-2 custom-table-pagination-container">
            <Row>
                <Col
                    xs={12}
                    md={6}
                    className="d-flex align-items-center justify-content-center justify-content-md-start px-4 mb-2 mb-md-0"
                >
                    <p className="my-0">{t("tables.numberOfRows")}</p>
                    <div className="mx-2 my-0">
                        <Form.Select
                            className="custom-form-control me-1"
                            style={{ width: "4.5rem" }}
                            aria-label={t("pagination.pageSize", tAreaNS)}
                            onChange={handlePageSizeChange}
                            value={pageSize}
                        >
                            {[5, 10, 15, 20, 100].map((size) => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </Form.Select>
                    </div>
                    <p className="my-0">
                        <Trans
                            i18nKey="tables.totalResults"
                            values={{
                                min: `${rangeStart}`,
                                max: `${rangeEnd}`,
                                totalCount,
                            }}
                        />
                    </p>
                </Col>
                <Col
                    xs={12}
                    md={6}
                    className="d-flex align-items-center justify-content-center justify-content-md-end px-4"
                >
                    {totalPages === 0 ? undefined : (
                        <>
                            <button
                                type="button"
                                className="me-2 btn btn-link p-0"
                                onClick={onPrevious}
                                disabled={currentPage === 1}
                                aria-label={t("button.previous", tAreaNS)}
                            >
                                <FaAngleDoubleLeft style={{ fontSize: "1.2rem" }} />
                            </button>
                            <div className="my-0">{`${currentPage} - ${totalPages}`}</div>
                            <button
                                type="button"
                                className="ms-2 btn btn-link p-0"
                                onClick={onNext}
                                disabled={currentPage >= totalPages}
                                aria-label={t("button.next", tAreaNS)}
                            >
                                <FaAngleDoubleRight style={{ fontSize: "1.2rem" }} />
                            </button>
                        </>
                    )}
                </Col>
            </Row>
        </div>
    );
};

export default Pagination;
